import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { bartlogin, bartkiln } from "../api/bartlett";
import { useAlert } from "./AlertContext";
import type { BartlettKilns, BartlettKiln } from "../types/bartlett";

const UNKNOWN_ERROR = "Unknown Error";

interface BartlettContextType {
  fetchBartlettKiln: (serial: string, username: string, password: string) => Promise<void>;
  getKilnStatus: (serial: string) => BartlettKiln | undefined;
  kilns: BartlettKilns;
}

const BartlettContext = createContext<BartlettContextType | undefined>(undefined);

export const BartlettProvider = ({ children }: { children: React.ReactNode }) => {
  const sessionRef = useRef<string | null>(null);
  const [kilns, setKilns] = useState<BartlettKilns>({});
  const { showError } = useAlert();

  const fetchBartlettKiln = useCallback(
    async (serial: string, username: string, password: string) => {
      // Login first
      let token = sessionRef.current;
      try {
        const response = await bartlogin.post("", {
          email: username,
          password: password,
        });
        token = response.data.authentication_token;
        sessionRef.current = token;
      } catch (err: any) {
        if (err && err.response && err.response.data) {
          const errorString = err.response.data.non_field_errors
            ? err.response.data.non_field_errors
            : UNKNOWN_ERROR;
          showError(errorString);
        } else {
          showError(UNKNOWN_ERROR);
        }
        return;
      }

      // Fetch kiln data
      try {
        const response = await bartkiln.post(
          `view?token=${token}&user_email=${username.toLowerCase()}`,
          { ids: [serial] },
          {
            headers: {
              "Auth-Token": `binst-cookie=${token}`,
              Email: username,
              "X-App-Name-Token": "kiln-aid",
              "Kaid-Version": "kaid-plus",
            },
          }
        );
        const indexed = response.data.kilns.reduce(
          (acc: BartlettKilns, kiln: BartlettKiln) => ({ ...acc, [kiln.serial_number]: kiln }),
          {}
        );
        setKilns((prev) => ({ ...prev, ...indexed }));
      } catch (err: any) {
        if (err && err.response && err.response.data) {
          const errorString = err.response.data.non_field_errors
            ? err.response.data.non_field_errors
            : UNKNOWN_ERROR;
          showError(errorString);
        } else {
          showError(UNKNOWN_ERROR);
        }
      }
    },
    [showError]
  );

  const getKilnStatus = useCallback(
    (serial: string) => {
      return kilns?.[serial];
    },
    [kilns]
  );

  return (
    <BartlettContext.Provider value={{ fetchBartlettKiln, getKilnStatus, kilns }}>
      {children}
    </BartlettContext.Provider>
  );
};

export const useBartlettContext = (): BartlettContextType => {
  const context = useContext(BartlettContext);
  if (!context) {
    throw new Error("useBartlettContext must be used within a BartlettProvider");
  }
  return context;
};

export default BartlettContext;
